import { Typography } from 'antd';
import { PageHeader, OmniChanelChatCustomer, CustomerLookupCall } from 'components';
import { useProfile, useTitle } from 'hooks';
import { sidebarMenuMessages } from 'messages';
import { PropsWithChildren, useCallback, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from 'store';
import { setIsCallSelected, setIsChatSelected, setInLookupMode } from 'store/features/customer-support-session/customer-support-session.slice';
import { twMerge } from 'tailwind-merge';
import { PERMISSION } from 'utils';
import { checkPermissions } from 'utils/check-permission';
import { getCurrentCallData } from 'pages/CustomerSupport/utils/sessionStorage';

function CustomerSupportLayout({ children }: PropsWithChildren) {
  const { userPermissionIds } = useProfile();
  const isAllowChatCustomer = checkPermissions(userPermissionIds, PERMISSION.CHAT_CUSTOMER);
  const isAllowAssignCustomer = checkPermissions(userPermissionIds, PERMISSION.ASSIGN_CHAT_CUSTOMER);
  const isAllowCallCustomer = checkPermissions(userPermissionIds, PERMISSION.CALL_CUSTOMER);

  useTitle(sidebarMenuMessages.customerSupport);
  const dispatch = useDispatch();
  const isCallSelected = useSelector((state: RootState) => state.customerSupportSession.isCallSelected);
  const isChatSelected = useSelector((state: RootState) => state.customerSupportSession.isChatSelected);

  // ✅ NEW: Auto-select chat if ONLY chat permission (no call permission)
  useEffect(() => {
    if (!isAllowCallCustomer && isAllowChatCustomer && !isChatSelected) {
      dispatch(setIsChatSelected(true));
      dispatch(setIsCallSelected(false));
      dispatch(setInLookupMode(true));

      // Auto-activate OmniChannelChatCustomer for chat-only users
      window.dispatchEvent(new CustomEvent('createDefaultChatTab', {
        detail: { timestamp: Date.now() }
      }));
    }
  }, [isAllowCallCustomer, isAllowChatCustomer, isChatSelected, dispatch]);

  // ✅ NEW: Auto-select call when user has both permissions (dual-permission default)
  useEffect(() => {
    if (isAllowCallCustomer && isAllowChatCustomer && !isCallSelected && !isChatSelected) {
      dispatch(setIsCallSelected(true));
      dispatch(setIsChatSelected(false));
    }
  }, [isAllowCallCustomer, isAllowChatCustomer, isCallSelected, isChatSelected, dispatch]);

  const updateIsCallSelected = async () => {
    // ✅ MODIFIED: Add check for switching mode state
    if (!isAllowCallCustomer) {
      return;
    }

    // ✅ MODIFIED: Remove loading states when switching to call
    try {
      await handleModeChange('call');
    } finally {
      // ✅ REMOVED: Loading cleanup steps
    }
  };

  const updateIsChatSelected = async () => {
    // ✅ MODIFIED: For dual-permission users, block manual chat activation
    if (!isAllowChatCustomer) {
      return;
    }

    // ✅ NEW: Block manual chat activation for dual-permission users
    if (isAllowCallCustomer && isAllowChatCustomer) {
      return;
    }

    await handleModeChange('chat');
  };

  // Enhance mode switching to ensure clean form transitions
  // ...existing code...
  const handleModeChange = useCallback(async (mode: 'call' | 'chat') => {

    // Dispatch pre-change event to allow components to save state
    window.dispatchEvent(new CustomEvent('preSessionModeChange', {
      detail: {
        newMode: mode,
        timestamp: Date.now(),
        // cleanForm: true // Let the specific mode logic decide if a full clean is needed
      }
    }));

    await new Promise(resolve => setTimeout(resolve, 100));

    if (mode === 'chat') {
      // Chat mode handling
      dispatch(setIsCallSelected(false));
      dispatch(setIsChatSelected(true));
      dispatch(setInLookupMode(true));

      window.dispatchEvent(new CustomEvent('createDefaultChatTab', {
        detail: { timestamp: Date.now() }
      }));

      // Ensure form is reset for chat mode if no active chat session dictates otherwise
      // This might need a more specific event if 'formFullReset' is too aggressive
      window.dispatchEvent(new CustomEvent('formFullReset', {
        detail: { timestamp: Date.now(), reason: 'modeChangeToChat' }
      }));


      window.dispatchEvent(new CustomEvent('chatSessionActivated', {
        detail: { timestamp: Date.now() }
      }));
    } else { // mode === 'call'
      if (!isAllowCallCustomer) {
        return;
      }

      dispatch(setIsCallSelected(true));
      dispatch(setIsChatSelected(false));
      dispatch(setInLookupMode(true));

      let activeCallPhoneNumber: string | undefined;
      let activeCallId: string | undefined;
      const existingCallData = getCurrentCallData();

      if (existingCallData && (existingCallData.callUuid !== 'placeholder-call' && existingCallData.callUuid !== '')) {
        activeCallPhoneNumber = existingCallData.customerNumber;
        activeCallId = existingCallData.callUuid || existingCallData.parentCallUuid;

        // Dispatch event to ensure form syncs with this active call if needed,
        // but avoid a full reset. The existing session change logic in CustomerLookupForm
        // should handle displaying this call's data when currentSessionId is set.
        // We just need to ensure the currentSessionId is correctly propagated.
        // The 'callSessionActivated' event might be sufficient if CustomerLookupForm's
        // session handling is robust.

        // If currentCallDialingEvent in localStorage is already set to this call,
        // and CustomerLookupForm's currentSessionId updates correctly, it should sync.
        // We can dispatch an event to specifically set the phone number if necessary,
        // without a full reset.
        if (activeCallPhoneNumber) {
          setTimeout(() => {
            window.dispatchEvent(new CustomEvent('setPhoneAndTriggerSearch', {
              detail: {
                timestamp: Date.now(),
                phoneNumber: activeCallPhoneNumber,
                callId: activeCallId // Pass callId to help CustomerLookupForm identify context
              }
            }));
          }, 150); // Delay to allow other state updates
        }

      } else {
        // Preserve phone number if one was somehow present in a placeholder or previous state
        activeCallPhoneNumber = existingCallData?.customerNumber;

        localStorage.setItem('currentCallDialingEvent', JSON.stringify({
          callUuid: 'placeholder-call', // Ensure a placeholder exists
          parentCallUuid: 'placeholder-call',
          customerNumber: activeCallPhoneNumber || '',
          timestamp: Date.now()
        }));

        if (!isCallSelected) {
          window.dispatchEvent(new CustomEvent('formFullReset', {
            detail: { timestamp: Date.now(), reason: 'modeChangeToCallNew' }
          }));

        }
        if (activeCallPhoneNumber) {
          setTimeout(() => {
            window.dispatchEvent(new CustomEvent('setPhoneAndTriggerSearch', {
              detail: {
                timestamp: Date.now(),
                phoneNumber: activeCallPhoneNumber
              }
            }));
          }, 200); // Increased delay after full reset
        }
      }

      window.dispatchEvent(new CustomEvent('callSessionActivated', {
        detail: { timestamp: Date.now(), callId: activeCallId || 'placeholder-call' }
      }));
    }

    // Post-change event with small delay
    // ...existing code...

    // Post-change event with small delay
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent('postSessionModeChange', {
        detail: {
          newMode: mode,
          timestamp: Date.now()
        }
      }));
    }, 100);
  }, [dispatch, isAllowCallCustomer]);

  return (
    <div>
      <PageHeader className='flex items-center gap-1'>
        <Typography.Title className='text-header-label mb-0 font-bold'>
          {sidebarMenuMessages.customerSupport}
        </Typography.Title>
      </PageHeader>

      <div className='container-list relative h-[calc(100vh-100px)] overflow-y-auto overflow-hidden border p-0'>
        <div className='flex max-h-full flex-1 overflow-hidden'>
          {(isAllowCallCustomer || isAllowChatCustomer) && (
            <div className='flex w-[400px] flex-col'>
              {/* ✅ Only show call section if user has call permission */}
              {isAllowCallCustomer && (
                <div
                  className={twMerge(
                    'flex-1 relative w-full rounded-tl-lg transition-all duration-300',
                    'cursor-pointer hover:bg-gray-50 hover:shadow-md opacity-100'
                  )}
                  onClick={() => {
                    updateIsCallSelected();
                  }}
                >
                  <CustomerLookupCall />
                  <div
                    className={twMerge(
                      'absolute inset-0 pointer-events-none rounded-tl-lg border border-solid transition-all duration-200',
                      isCallSelected
                        ? 'border-l-blue-500 border-t-blue-500 border-r-transparent border-b-transparent glow-border-left'
                        : 'border-l-gray-300 border-t-gray-300 border-r-transparent border-b-transparent'
                    )}
                  ></div>
                </div>
              )}

              <div
                className={twMerge(
                  'relative w-full border-r border-t border-solid transition-all duration-300',
                  // ✅ MODIFIED: Adjust height and click behavior based on permissions
                  isAllowCallCustomer
                    ? (isAllowChatCustomer ? 'h-full' : isAllowAssignCustomer ? 'h-[124px]' : 'h-auto')
                    : 'h-full', // Take full height if no call permission
                  // ✅ MODIFIED: Different cursor behavior for dual vs single permission
                  !isAllowChatCustomer
                    ? 'cursor-not-allowed opacity-60'
                    : (isAllowCallCustomer && isAllowChatCustomer)
                      ? 'cursor-default opacity-100' // No hover for dual-permission
                      : 'cursor-pointer hover:bg-gray-50 hover:shadow-md opacity-100' // Hover for chat-only
                )}
                onClick={() => {
                  if (!isAllowChatCustomer) return;
                  if (isChatSelected) return;

                  // ✅ MODIFIED: Only allow click for chat-only users
                  if (!isAllowCallCustomer) {
                    updateIsChatSelected();
                  }
                  // For dual-permission users, do nothing on click
                }}
              >
                <OmniChanelChatCustomer />
                <div
                  className={twMerge(
                    'absolute inset-0 pointer-events-none border border-solid transition-all duration-200',
                    isChatSelected
                      ? 'border-b-blue-500 border-l-blue-500 border-t-transparent border-r-transparent glow-border-bottom glow-border-left'
                      : 'border-transparent'
                  )}
                ></div>
              </div>
            </div>
          )}

          <div
            className={twMerge(
              'relative',
              isAllowCallCustomer || isAllowChatCustomer ? 'w-[calc(100%-400px)]' : 'w-full'
            )}
          >
            <div className='slim-scrollbar slim-scrollbar-horizontal max-h-full overflow-auto min-h-0'>
              {children}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CustomerSupportLayout;
